import type { TFunction } from 'i18next';

import i18n from '../i18n';
import {
  groupLanes,
  groupTracks,
  hoursType,
  specialTagType,
} from '../data/orders';

/**
 * Etiquetas TRADUCIDAS del catálogo estático (2026-08-04).
 *
 * ── El problema ──────────────────────────────────────────────────────────
 * `src/data/orders.ts` lista cuatro enums cerrados —Carril, Tipo de vía,
 * Tipo de horas y Etiqueta especial— con el texto escrito A MANO Y EN
 * CATALÁN. Nunca pasaba por i18n, así que salía en catalán también con la
 * aplicación en castellano o en inglés: en pantalla, en los PDF y en los
 * Excel. Se cazó sacando el informe del RV en inglés (prueba u15).
 *
 * ── La regla ─────────────────────────────────────────────────────────────
 * Se mapea el `value` —el enum, lo que viaja al backend y está guardado en
 * Mongo— a una clave i18n. NUNCA se traduce texto→texto, y NUNCA se
 * interpola la clave desde el valor: hay valores con espacios y con un punto
 * ('input branch', 'building cons.'). El `value` no se toca jamás; el test
 * `tests/e2e/specs/actions/catalog-i18n.spec.ts` lo congela.
 *
 * ── Por qué SIEMPRE hay `defaultValue` ───────────────────────────────────
 * Las traducciones se cargan por HTTP. Sin cobertura, o antes de que
 * lleguen, `t()` devuelve la clave en crudo — ya nos pasó con la PWA, que
 * arrancaba enseñando las claves por pantalla. Con `defaultValue` apuntando
 * al nombre del catálogo, el peor caso es ver catalán: exactamente lo que se
 * veía antes de este cambio.
 *
 * ── Valores desconocidos ─────────────────────────────────────────────────
 * Un valor que no esté en el mapa —una sigla como ADR, un dato antiguo, o el
 * catálogo inventado que inyectan algunos tests— cae a su propio `name`. La
 * función nunca devuelve vacío por no reconocer algo.
 *
 * ── De dónde viene ───────────────────────────────────────────────────────
 * Generaliza el piloto `utils/laneLabels.ts` (PR #138), que traducía solo el
 * carril en los tres modales del RV, y sigue el patrón ya canónico de
 * `directionNameKey()` en `utils/surveillanceRoute.ts`.
 */

export type CatalogEntry = { id?: string; name: string; value: string };
export type Option = { label: string; value: string };

/**
 * La `t` de react-i18next o cualquier equivalente. Es OPCIONAL: cuando se
 * omite se usa la instancia global de i18next, que es lo que necesitan los
 * *selectors*, los `passData.ts` y el generador de PDF, que corren fuera de
 * React. Dentro de React conviene pasarla, para que el `useMemo` que la
 * envuelve tenga una dependencia honesta y las etiquetas sigan al cambio de
 * idioma.
 */
export type Translator =
  | TFunction
  | ((key: string, options?: { defaultValue?: string }) => string);

type PlainTranslator = (key: string, options?: { defaultValue?: string }) => string;

const globalT: PlainTranslator = (key, options) =>
  i18n.t(key, options as Record<string, unknown>);

// ── Mapas value → clave ────────────────────────────────────────────────────

/** `unspecified` reutiliza una clave que ya existía en los tres idiomas. */
export const LANE_KEYS: Record<string, string> = {
  right: 'forms:lanes.right',
  left: 'forms:lanes.left',
  central: 'forms:lanes.central',
  both: 'forms:lanes.both',
  unspecified: 'common:workOrder.unspecified',
};

/**
 * Ojo: el «sin especificar» de Tipo de vía NO usa el mismo valor que el de
 * Carril. Aquí el valor es el catalán `'sense especificar'` (así está
 * guardado en base de datos); en Carril es `'unspecified'`.
 */
export const TRACK_KEYS: Record<string, string> = {
  trunk: 'forms:tracks.trunk',
  roundabout: 'forms:tracks.roundabout',
  'input branch': 'forms:tracks.inputBranch',
  'exit branch': 'forms:tracks.exitBranch',
  'service road': 'forms:tracks.serviceRoad',
  roadway: 'forms:tracks.roadway',
  shoulder: 'forms:tracks.shoulder',
  'off roadway': 'forms:tracks.offRoadway',
  tunnel: 'forms:tracks.tunnel',
  link: 'forms:tracks.link',
  'building cons.': 'forms:tracks.buildingCons',
  'service area': 'forms:tracks.serviceArea',
  'sense especificar': 'common:workOrder.unspecified',
};

/**
 * Siglas idénticas en los tres idiomas: NO llevan clave a propósito. Crear 39
 * entradas para copiar la misma sigla tres veces sería ruido. El tripwire
 * `catalog-i18n.spec.ts` las acepta gracias a esta lista blanca.
 */
export const TRACK_ACRONYMS: string[] = [
  'ADR',
  'JC',
  'FF',
  'SJ',
  'RM',
  'BC',
  'ES',
  'CR',
  'PG',
  'LC',
  'JT',
  'BG',
  'LF',
];

/**
 * La clave se nombra por el VALUE, no por el texto. Por eso `normalExtrasRV`
 * lleva el texto «Extres normals VH»: el catálogo arrastra esa incoherencia
 * desde siempre (valor RV, texto VH) y este módulo no la arregla — el valor
 * está guardado en base de datos y cambiarlo sería una migración.
 */
export const HOURS_TYPE_KEYS: Record<string, string> = {
  normal: 'forms:hoursTypes.normal',
  normalExtras: 'forms:hoursTypes.normalExtras',
  holidayExtras: 'forms:hoursTypes.holidayExtras',
  holidayExtrasRV: 'forms:hoursTypes.holidayExtrasRV',
  normalExtrasRV: 'forms:hoursTypes.normalExtrasRV',
  holidayExtrasVH: 'forms:hoursTypes.holidayExtrasVH',
  'sense especificar': 'common:workOrder.unspecified',
};

/** Cero claves nuevas: `common:orderTypes.*` ya decía exactamente esto. */
export const SPECIAL_TAG_KEYS: Record<string, string> = {
  surveillanceRoute: 'common:orderTypes.surveillanceRoute',
  campaign: 'common:orderTypes.campaign',
};

// ── Motor ──────────────────────────────────────────────────────────────────

const labelFromCatalog = (
  catalog: CatalogEntry[],
  keys: Record<string, string>,
  value: string | null | undefined,
  t: Translator
): string => {
  if (!value) return '';
  const fallback = catalog.find((e) => e.value === value)?.name ?? value;
  const key = keys[value];
  return key ? (t as PlainTranslator)(key, { defaultValue: fallback }) : fallback;
};

/**
 * Traduce una lista de opciones que puede venir de FUERA del catálogo
 * importado: los formularios las reciben por `defaultValues`, y esa lista
 * viaja por `sessionStorage` (wizards a medias) y por los mocks de los tests.
 * Por eso el respaldo es el `name` de la propia entrada, no el del catálogo.
 */
const translateOptions = (
  entries: CatalogEntry[] | undefined,
  keys: Record<string, string>,
  t: Translator
): Option[] =>
  (entries ?? []).map((entry) => {
    const key = keys[entry.value];
    return {
      label: key
        ? (t as PlainTranslator)(key, { defaultValue: entry.name })
        : entry.name,
      value: entry.value,
    };
  });

// ── API pública ────────────────────────────────────────────────────────────

export const laneLabel = (
  value?: string | null,
  t: Translator = globalT
): string => labelFromCatalog(groupLanes, LANE_KEYS, value, t);

export const trackLabel = (
  value?: string | null,
  t: Translator = globalT
): string => labelFromCatalog(groupTracks, TRACK_KEYS, value, t);

export const hoursTypeLabel = (
  value?: string | null,
  t: Translator = globalT
): string => labelFromCatalog(hoursType, HOURS_TYPE_KEYS, value, t);

export const specialTagLabel = (
  value?: string | null,
  t: Translator = globalT
): string => labelFromCatalog(specialTagType, SPECIAL_TAG_KEYS, value, t);

export const getLaneOptions = (t: Translator = globalT): Option[] =>
  translateOptions(groupLanes, LANE_KEYS, t);

export const getTrackOptions = (t: Translator = globalT): Option[] =>
  translateOptions(groupTracks, TRACK_KEYS, t);

export const translateLaneOptions = (
  entries: CatalogEntry[] | undefined,
  t: Translator = globalT
): Option[] => translateOptions(entries, LANE_KEYS, t);

export const translateTrackOptions = (
  entries: CatalogEntry[] | undefined,
  t: Translator = globalT
): Option[] => translateOptions(entries, TRACK_KEYS, t);

export const translateHoursTypeOptions = (
  entries: CatalogEntry[] | undefined,
  t: Translator = globalT
): Option[] => translateOptions(entries, HOURS_TYPE_KEYS, t);

export const translateSpecialTagOptions = (
  entries: CatalogEntry[] | undefined,
  t: Translator = globalT
): Option[] => translateOptions(entries, SPECIAL_TAG_KEYS, t);
